import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ChevronRight, Check, Target } from 'lucide-react';

/**
 * Advanced Spotlight Tutorial Component
 * Highlights specific UI elements and provides interactive explanations
 */
export default function SpotlightTutorial({ steps, isPresented, onComplete }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showContent, setShowContent] = useState(false);
  const [highlightRect, setHighlightRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const overlayRef = useRef(null);
  const highlightedElementRef = useRef(null);
  const location = useLocation();

  // Track if tutorial was already started to prevent reset on navigation
  const tutorialStartedRef = useRef(false);
  const stepsRef = useRef(steps);
  
  useEffect(() => {
    if (isPresented && steps.length > 0) {
      // Only reset if tutorial wasn't started yet
      if (!tutorialStartedRef.current) {
        setCurrentStepIndex(0);
        tutorialStartedRef.current = true;
      }
      setShowContent(true);
      highlightElement(steps[currentStepIndex]);
    } else {
      setShowContent(false);
      setHighlightRect(null);
      tutorialStartedRef.current = false;
    }
  }, [isPresented]);
  
  // Keep steps ref current so location-change effect uses latest steps
  useEffect(() => { stepsRef.current = steps; }, [steps]);

  // Re-highlight the current step after route navigation completes
  useEffect(() => {
    if (!isPresented || !tutorialStartedRef.current || stepsRef.current.length === 0) return;
    const step = stepsRef.current[currentStepIndex];
    if (!step) return;
    const timer = setTimeout(() => {
      highlightElement(step);
      setShowContent(true);
    }, 800);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Handle step changes separately
  useEffect(() => {
    if (isPresented && steps.length > 0 && tutorialStartedRef.current) {
      const currentStep = steps[currentStepIndex];
      highlightElement(currentStep);
    }
  }, [currentStepIndex]);

  useEffect(() => {
    if (isPresented && steps.length > 0 && tutorialStartedRef.current) {
      const currentStep = steps[currentStepIndex];
      
      // Check if step should be shown on current page
      const shouldShow = shouldShowStep(currentStep);
      
      if (!shouldShow && currentStep.action && typeof currentStep.action === 'function') {
        // Step not visible on current page, execute navigation action
        setTimeout(() => {
          currentStep.action();
          // Wait for navigation, then try to highlight
          setTimeout(() => {
            highlightElement(currentStep);
          }, 1200);
        }, 300);
      } else if (shouldShow) {
        // Step should be shown, execute action if present
        if (currentStep.action && typeof currentStep.action === 'function') {
          // Don't execute action if we're already on the target page
          const isAlreadyOnTargetPage = currentStep.targetSelector && document.querySelector(currentStep.targetSelector);
          if (currentStep.forceAction || !isAlreadyOnTargetPage) {
            setTimeout(() => {
              currentStep.action();
              setTimeout(() => {
                highlightElement(currentStep);
              }, 1200);
            }, 300);
          } else {
            highlightElement(currentStep);
          }
        } else {
          highlightElement(currentStep);
        }
      } else {
        // Step not available on current page, skip to next
        // But wait a bit longer to allow for navigation to complete
        if (currentStepIndex < steps.length - 1) {
          setTimeout(() => {
            // Find next valid step
            let nextIndex = currentStepIndex + 1;
            let attempts = 0;
            const maxAttempts = 10;
            
            while (nextIndex < steps.length && attempts < maxAttempts) {
              if (shouldShowStep(steps[nextIndex])) {
                setCurrentStepIndex(nextIndex);
                return;
              }
              nextIndex++;
              attempts++;
            }
            
            // If we couldn't find a valid step, wait a bit more (might be navigating)
            if (attempts < maxAttempts && nextIndex < steps.length) {
              setTimeout(() => {
                // Try one more time after navigation completes
                if (shouldShowStep(steps[nextIndex])) {
                  setCurrentStepIndex(nextIndex);
                } else {
                  completeTutorial();
                }
              }, 1500);
            } else {
              // No more valid steps, complete tutorial
              completeTutorial();
            }
          }, 1000); // Increased wait time for navigation
        } else {
          completeTutorial();
        }
      }
    }
  }, [currentStepIndex, isPresented]);

  // Track current step to determine if we should show steps based on location
  const shouldShowStep = (step) => {
    // Steps with actions (navigation) should always be shown
    if (step.action && typeof step.action === 'function') {
      return true;
    }
    
    // If step has a targetSelector, check if element exists on current page
    if (step.targetSelector) {
      const element = document.querySelector(step.targetSelector);
      return element !== null;
    }
    
    // Steps without targetSelector (like welcome/final) always show
    return true;
  };

  const highlightElement = (step) => {
    if (!step.targetSelector) {
      setHighlightRect(null);
      return;
    }

    // Wait for DOM to be ready, with retry logic
    let retries = 0;
    const maxRetries = 10;
    
    const tryHighlight = () => {
      const element = document.querySelector(step.targetSelector);
      
      if (element) {
        // Scroll element into view
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'center'
        });

        // Wait for scroll to complete
        setTimeout(() => {
          const rect = element.getBoundingClientRect();
          const padding = step.padding || 8;
          
          setHighlightRect({
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + (padding * 2),
            height: rect.height + (padding * 2),
          });

          // Calculate tooltip position
          const tooltipPos = calculateTooltipPosition(rect, step.position || 'bottom');
          setTooltipPosition(tooltipPos);

          highlightedElementRef.current = element;
          
          // Add pulse animation to highlighted element
          element.style.transition = 'all 0.3s ease';
          element.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.5)';
          
          // Remove pulse after animation
          setTimeout(() => {
            if (element) {
              element.style.boxShadow = '';
            }
          }, 500);
        }, 300);
      } else {
        // Retry if element not found (might still be loading)
        retries++;
        if (retries < maxRetries) {
          setTimeout(tryHighlight, 200);
        } else {
          console.warn(`Tutorial: Element not found for selector: ${step.targetSelector} after ${maxRetries} retries`);
          setHighlightRect(null);
        }
      }
    };
    
    setTimeout(tryHighlight, 100);
  };

  const calculateTooltipPosition = (rect, position) => {
    const spacing = 20;
    const tooltipWidth = 400;
    const tooltipHeight = 200;

    switch (position) {
      case 'top':
        return {
          top: rect.top - tooltipHeight - spacing,
          left: rect.left + (rect.width / 2) - (tooltipWidth / 2),
        };
      case 'bottom':
        return {
          top: rect.bottom + spacing,
          left: rect.left + (rect.width / 2) - (tooltipWidth / 2),
        };
      case 'left':
        return {
          top: rect.top + (rect.height / 2) - (tooltipHeight / 2),
          left: rect.left - tooltipWidth - spacing,
        };
      case 'right':
        return {
          top: rect.top + (rect.height / 2) - (tooltipHeight / 2),
          left: rect.right + spacing,
        };
      default:
        return {
          top: rect.bottom + spacing,
          left: rect.left + (rect.width / 2) - (tooltipWidth / 2),
        };
    }
  };

  const handleNext = () => {
    // Clean up current highlight
    if (highlightedElementRef.current) {
      highlightedElementRef.current.style.boxShadow = '';
      highlightedElementRef.current = null;
    }

    if (currentStepIndex < steps.length - 1) {
      setShowContent(false);
      setTimeout(() => {
        // Find next step that should be shown
        let nextIndex = currentStepIndex + 1;
        while (nextIndex < steps.length) {
          if (shouldShowStep(steps[nextIndex])) {
            setCurrentStepIndex(nextIndex);
            setShowContent(true);
            return;
          }
          nextIndex++;
        }
        // If no more steps to show, complete tutorial
        completeTutorial();
      }, 200);
    } else {
      completeTutorial();
    }
  };

  const handleSkip = () => {
    completeTutorial();
  };

  const completeTutorial = () => {
    // Clean up
    if (highlightedElementRef.current) {
      highlightedElementRef.current.style.boxShadow = '';
      highlightedElementRef.current = null;
    }
    
    setShowContent(false);
    setHighlightRect(null);
    setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, 300);
  };

  if (!isPresented || steps.length === 0) {
    return null;
  }

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-[9999]"
      style={{ 
        pointerEvents: highlightRect ? 'none' : 'auto' // Allow clicks through when highlighting
      }}
    >
      {/* Blurred overlay - everything outside highlight is blurred */}
      {highlightRect && (
        <div 
          className="absolute inset-0 backdrop-blur-md transition-opacity duration-300"
          style={{ 
            opacity: showContent ? 1 : 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            pointerEvents: 'auto', // Block clicks outside highlight
            clipPath: `polygon(
              0% 0%, 
              0% 100%, 
              ${highlightRect.left}px 100%, 
              ${highlightRect.left}px ${highlightRect.top}px, 
              ${highlightRect.left + highlightRect.width}px ${highlightRect.top}px, 
              ${highlightRect.left + highlightRect.width}px ${highlightRect.top + highlightRect.height}px, 
              ${highlightRect.left}px ${highlightRect.top + highlightRect.height}px, 
              ${highlightRect.left}px 100%, 
              100% 100%, 
              100% 0%
            )`,
          }}
          onClick={(e) => {
            // Block clicks outside highlighted area
            e.stopPropagation();
          }}
        />
      )}
      
      {/* Dark overlay with cutout - only outside highlight area */}
      {highlightRect && (
        <svg 
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: 'none' }}
        >
          <defs>
            <mask id={`spotlight-mask-${currentStepIndex}`}>
              <rect width="100%" height="100%" fill="black" />
              <rect
                x={highlightRect.left}
                y={highlightRect.top}
                width={highlightRect.width}
                height={highlightRect.height}
                fill="white"
                rx="12"
              />
            </mask>
          </defs>
          <rect 
            width="100%" 
            height="100%" 
            fill="rgba(0, 0, 0, 0.4)" 
            mask={`url(#spotlight-mask-${currentStepIndex})`}
            className="transition-opacity duration-300"
            style={{ opacity: showContent ? 1 : 0 }}
          />
        </svg>
      )}

      {/* Highlight border - no boxShadow here, blur is handled by overlay */}
      {highlightRect && (
        <div
          className="absolute border-4 border-blue-500 rounded-xl pointer-events-none transition-all duration-300 z-10"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.6)',
            opacity: showContent ? 1 : 0,
          }}
        >
          {/* Pulse animation */}
          <div className="absolute inset-0 border-4 border-blue-400 rounded-xl animate-ping opacity-75" />
        </div>
      )}

      {/* Tooltip/Balloon */}
      {currentStep.targetSelector && highlightRect && (
        <div
          className={`tutorial-tooltip absolute bg-white rounded-2xl shadow-2xl p-6 max-w-md pointer-events-auto transition-all duration-300 ${
            showContent ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
          style={{
            top: `${Math.max(20, Math.min(tooltipPosition.top, window.innerHeight - 300))}px`,
            left: `${Math.max(20, Math.min(tooltipPosition.left, window.innerWidth - 420))}px`,
            zIndex: 10000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Arrow pointing to element */}
          <div
            className="absolute w-0 h-0 border-8 border-transparent"
            style={{
              ...(currentStep.position === 'top' && {
                bottom: '-16px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderTopColor: 'white',
              }),
              ...(currentStep.position === 'bottom' && {
                top: '-16px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderBottomColor: 'white',
              }),
              ...(currentStep.position === 'left' && {
                right: '-16px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderLeftColor: 'white',
              }),
              ...(currentStep.position === 'right' && {
                left: '-16px',
                top: '50%',
                transform: 'translateY(-50%)',
                borderRightColor: 'white',
              }),
            }}
          />

          {/* Content */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {currentStep.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {currentStep.description}
              </p>
              <div className="flex gap-2">
                {!isLastStep && (
                  <button
                    onClick={handleSkip}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Overslaan
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className={`px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 ${
                    isLastStep ? 'w-full justify-center' : ''
                  }`}
                >
                  {isLastStep ? (
                    <>
                      <Check className="w-4 h-4" />
                      Begrepen!
                    </>
                  ) : (
                    <>
                      Volgende
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Center tooltip (when no target element) */}
      {(!currentStep.targetSelector || !highlightRect) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div
            className={`tutorial-tooltip bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-4 pointer-events-auto transition-all duration-300 ${
              showContent ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-5'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Progress indicator */}
            <div className="flex gap-1 mb-4">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded transition-all duration-300 ${
                    index <= currentStepIndex ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
                <span className="text-3xl">
                  {currentStep.icon === 'house.fill' && '🏠'}
                  {currentStep.icon === 'magnifyingglass' && '🔍'}
                  {currentStep.icon === 'star.fill' && '⭐'}
                  {currentStep.icon === 'calendar' && '📅'}
                  {currentStep.icon === 'bubble.left.and.bubble.right.fill' && '💬'}
                  {currentStep.icon === 'person.crop.circle.fill' && '👤'}
                  {currentStep.icon === 'checkmark.circle.fill' && '✅'}
                  {currentStep.icon === 'building.2.fill' && '🏢'}
                  {currentStep.icon === 'plus.circle.fill' && '➕'}
                  {currentStep.icon === 'key.fill' && '🔑'}
                  {!['house.fill', 'magnifyingglass', 'star.fill', 'calendar', 'bubble.left.and.bubble.right.fill', 'person.crop.circle.fill', 'checkmark.circle.fill', 'building.2.fill', 'plus.circle.fill', 'key.fill'].includes(currentStep.icon) && 'ℹ️'}
                </span>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-center mb-2">
              {currentStep.title}
            </h3>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6 text-sm leading-relaxed">
              {currentStep.description}
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="flex-1 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                  Overslaan
                </button>
              )}
              <button
                onClick={handleNext}
                className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm ${
                  isLastStep ? 'w-full' : ''
                }`}
              >
                {isLastStep ? (
                  <>
                    <Check className="w-4 h-4" />
                    Begrepen!
                  </>
                ) : (
                  <>
                    Volgende
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

